import * as Sentry from "@sentry/react";
import { Session, User } from "@supabase/supabase-js";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState
} from "react";

import { usePostHog } from "../../common/posthog";
import {
  resetPostHogIdentity
} from "../../common/posthog/posthog-client";
import { supabase } from "../../data/supabase/client";
import { logError } from "../../utils/error";
import { clearPreviousSession } from "../explore/utils/ragchatStorage";
import { decodeJWT } from "./jwt";

const setSentryUserContext = (user: User | null, tenantId: string | null) => {
  Sentry.setUser(user?.id ? { id: user.id, email: user.email } : null);
  Sentry.setTag("tenant_id", tenantId ?? undefined);
};

const extractPermittedEmailsFromClaims = (analysisGroups: Record<string, string[]> | null): string[] | null => {
  if (!analysisGroups || Object.keys(analysisGroups).length === 0) {
    return null;
  }

  const allEmails = Object.values(analysisGroups)
    .flat()
    .map(email => email.toLowerCase())
    .filter((email, index, array) => array.indexOf(email) === index);

  return allEmails.length > 0 ? allEmails : null;
};

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
  tenantId: string | null;
  tenantName: string | null;
  jwtClaims: Record<string, any>;
  authError: string | null;
  permittedEmails: string[] | null;
  analysisGroups: Record<string, string[]> | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const posthog = usePostHog();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [jwtClaims, setJwtClaims] = useState<Record<string, any>>({});
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [permittedEmails, setPermittedEmails] = useState<string[] | null>(null);
  const [analysisGroups, setAnalysisGroups] = useState<Record<string, string[]> | null>(null);

  // Parse JWT claims and extract tenant info
  const parseJwtAndExtractTenantInfo = (session: Session | null) => {
    if (!session?.access_token) {
      setJwtClaims({});
      setTenantId(null);
      setTenantName(null);
      setPermittedEmails(null);
      setAnalysisGroups(null);
      setSentryUserContext(null, null);
      return;
    }

    const claims = decodeJWT(session.access_token) as Record<string, any> | null;

    if (claims === null) {
      setJwtClaims({});
      setTenantId(null);
      setTenantName(null);
      setAnalysisGroups(null);
      setPermittedEmails(null);
      setSentryUserContext(session.user, null);
      setAuthError("Unable to read session token");
      supabase.auth.signOut().catch(err => {
        logError("Error signing out", err);
      });
      return;
    }

    setJwtClaims(claims);

    const accountId = claims.tenant_ids?.[0] || null;
    const accountName = claims.account_name || null;

    setTenantId(accountId);
    setTenantName(accountName);
    setSentryUserContext(session.user, accountId);

    const jwtAnalysisGroups = claims.analysis_groups as Record<string, string[]> | null;
    setAnalysisGroups(jwtAnalysisGroups);

    const emails = extractPermittedEmailsFromClaims(jwtAnalysisGroups);
    setPermittedEmails(emails);

    const isRecoverySession = claims.amr?.some(
      (entry: string | { method: string; timestamp?: number }) =>
        (typeof entry === "string" ? entry : entry.method) === "otp"
    );

    if (session && !accountId && !isRecoverySession) {
      setAuthError("Unable to log you in - no associated account");
      supabase.auth.signOut().catch(err => {
        logError("Error signing out", err);
      });
    } else {
      setAuthError(null);
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      parseJwtAndExtractTenantInfo(session);
      setIsLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      parseJwtAndExtractTenantInfo(session);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      posthog.capture("user_signed_out");

      resetPostHogIdentity(posthog);
      clearPreviousSession();

      await supabase.auth.signOut();
      setSession(null);
      setTenantId(null);
      setTenantName(null);
      setJwtClaims({});
      setPermittedEmails(null);
      setAnalysisGroups(null);
      setSentryUserContext(null, null);

    } catch (error) {
      logError("Error signing out", error);
    }
  };

  const value = {
    session,
    user: session?.user ?? null,
    isLoading,
    signOut,
    tenantId,
    tenantName,
    jwtClaims,
    authError,
    permittedEmails,
    analysisGroups
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
